import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'oidc-react';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import { useToast } from '../../providers/ToastProvider/ToastContext';
import { useRemediationPlanService } from '../services/useRemediationPlanService';
import api from '../services/apiService';
import Project from '../../common/Utils/project';
import { LoaderCircle, AlertCircle } from 'lucide-react';

interface RemediationPlanDetail {
  remediationPlanID: string;
  planName: string;
  remediationPlanStartTime: string;
  agentInfo?: Array<{
    agentStatus: string;
    agentCount: number;
  }>;
  remediationPlanStatus?: string;
}

interface ApprovalFromEmailModalProps {
  planId: string;
}

const ApprovalFromEmailModal: React.FC<ApprovalFromEmailModalProps> = ({ planId }) => {
  const navigate = useNavigate();
  const auth = useAuth();
  const { selectedTenantId } = useTenantSelection();
  const { addToast } = useToast();
  const { updateRemediationPlanStatus } = useRemediationPlanService();

  const [planDetail, setPlanDetail] = useState<RemediationPlanDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Fetch plan details on mount
  useEffect(() => {
    const fetchPlanDetail = async () => {
      try {
        setIsLoading(true);
        setError(null);

        if (!auth?.userData || !selectedTenantId) {
          setError('Authentication required. Please log in again.');
          return;
        }

        // Fetch plans from existing grouped-list endpoint and locate this plan by id.
        const response = await api.get(`patchcenterservice/remediationplan/${selectedTenantId}/remediation-plan-list`);

        const groupedPlans = (response?.data?.data || []) as Array<{ remediationPlans?: RemediationPlanDetail[] }>;
        const matchedPlan = groupedPlans
          .flatMap((group: { remediationPlans?: RemediationPlanDetail[] }) => group?.remediationPlans || [])
          .find((plan: RemediationPlanDetail) => String(plan?.remediationPlanID || '').toLowerCase() === planId.toLowerCase());

        if (matchedPlan) {
          setPlanDetail(matchedPlan);
        } else {
          setError('Plan not found or you do not have permission to access it.');
        }
      } catch (err) {
        console.error('Error fetching plan detail:', err);
        setError('Failed to load plan details. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPlanDetail();
  }, [planId, auth, selectedTenantId]);

  const handleApprove = async () => {
    try {
      if (!selectedTenantId) {
        addToast('Tenant is not selected. Please select a tenant and try again.', 'error', 4000);
        return;
      }

      setIsProcessing(true);
      const result = await updateRemediationPlanStatus(
        selectedTenantId,
        planId,
        'Approved',
        (planDetail?.remediationPlanStatus as 'Created' | 'SubmittedForApproval') || 'SubmittedForApproval',
      );

      if (result?.isSuccess && result?.data) {
        addToast('Plan approved successfully', 'success', 4000);
        setTimeout(() => {
          navigate('/patchCenter');
        }, 2000);
      } else {
        addToast(result?.message || 'Approval failed. Plan status was not updated.', 'error', 4000);
      }
    } catch (err) {
      console.error('Error approving plan:', err);
      addToast('Failed to approve plan. Please try again.', 'error', 4000);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    try {
      if (!selectedTenantId) {
        addToast('Tenant is not selected. Please select a tenant and try again.', 'error', 4000);
        return;
      }

      setIsProcessing(true);
      const result = await updateRemediationPlanStatus(
        selectedTenantId,
        planId,
        'ApprovalRejected',
        (planDetail?.remediationPlanStatus as 'Created' | 'SubmittedForApproval') || 'SubmittedForApproval',
      );

      if (result?.isSuccess && result?.data) {
        addToast('Plan rejected successfully', 'success', 4000);
        setTimeout(() => {
          navigate('/patchCenter');
        }, 2000);
      } else {
        addToast(result?.message || 'Rejection failed. Plan status was not updated.', 'error', 4000);
      }
    } catch (err) {
      console.error('Error rejecting plan:', err);
      addToast('Failed to reject plan. Please try again.', 'error', 4000);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm transition-opacity px-4">
      <div className="w-full max-w-xl rounded-xl bg-[#1c1c1c] p-6 text-white shadow-2xl border border-gray-700">
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-10">
            <LoaderCircle className="w-10 h-10 animate-spin text-blue-500 mb-4" />
            <p className="text-sm text-gray-300">Loading plan details...</p>
          </div>
        )}

        {error && !isLoading && (
          <div className="flex flex-col items-center justify-center py-4">
            <AlertCircle className="w-10 h-10 text-red-500 mb-4" />
            <p className="text-sm text-red-300 text-center mb-6">{error}</p>
            <button
              onClick={() => navigate('/patchCenter')}
              className="px-5 py-2 border border-gray-600 rounded-full text-gray-300 hover:bg-gray-700 transition-all"
            >
              Back to Patch Center
            </button>
          </div>
        )}

        {!isLoading && !error && planDetail && (
          <>
            <div className="flex items-center justify-between border-b border-gray-700 pb-3 mb-4">
              <h3 className="text-lg font-semibold">Plan Approval</h3>
            </div>

            <p className="text-sm text-gray-300 mb-4">Review the remediation plan details below before approving or rejecting it.</p>

            <div className="space-y-4 mb-6 rounded-lg border border-gray-700 bg-[#171717] p-4">
              <div>
                <label className="text-xs uppercase tracking-wide text-gray-400">Plan Name</label>
                <p className="mt-1 text-sm font-medium text-white">{planDetail.planName}</p>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-xs uppercase tracking-wide text-gray-400">Status</label>
                  <p className="mt-1 text-sm font-medium text-white">{planDetail.remediationPlanStatus || 'Pending'}</p>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-wide text-gray-400">Total Devices</label>
                  <p className="mt-1 text-sm font-medium text-white">
                    {planDetail.agentInfo?.reduce((sum, info) => sum + info.agentCount, 0) || 0}
                  </p>
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-wide text-gray-400">Scheduled Start</label>
                <p className="mt-1 text-sm font-medium text-white">
                  {new Date(planDetail.remediationPlanStartTime).toLocaleDateString('en-GB', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => navigate('/patchCenter')}
                disabled={isProcessing}
                className="px-5 py-2 border border-gray-600 rounded-full text-gray-300 hover:bg-gray-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={isProcessing}
                className="px-5 py-2 rounded-full bg-gray-700 hover:bg-gray-800 font-medium transition-all text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? 'Processing...' : 'Reject'}
              </button>
              <button
                onClick={handleApprove}
                disabled={isProcessing}
                className="px-5 py-2 rounded-full bg-red-700 hover:bg-red-800 font-medium transition-all text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? 'Processing...' : 'Approve'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ApprovalFromEmailModal;
